import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";

type EmployeeRow = {
  id: string;
  empNo: string;
  name: string;
  category?: string;
  designation: string;
  joiningDate?: string;
  lastPromoDate?: string;
  serviceYears: number | string;
  aparTotal: string;
  aparRemarks?: string;
  dar: string;
  vigilance: string;
  effectiveDate: string;
  listClass: string;
  dpcStatus: string;
  dpcRemarks?: string;
  approvalStatus: string;
  [key: string]: any;
};

const formatAparScore = (val: any): string => {
  if (val === undefined || val === null || val === "" || val === "—") return "-";
  const str = String(val).trim();
  const num = parseFloat(str);
  if (!isNaN(num) && num === 0) return "-";
  if (str === "0" || str === "0.0" || str === "0.00") return "-";
  return str;
};

const DpcApprovalList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const stateCycleId = location.state?.cycleId;
  const paramCycleId = queryParams.get("cycleId");
  const initialCycleId = stateCycleId || (paramCycleId ? Number(paramCycleId) : 1);

  const [selectedCycleId, setSelectedCycleId] = useState<number>(initialCycleId);
  const [query, setQuery] = useState<string>("");
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [cyclesList, setCyclesList] = useState<any[]>([
    { id: 1, name: " " }
  ]);

  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"apar" | "dar" | "vigilance">("apar");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const tableRef = useRef<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [selectedBulkRemark, setSelectedBulkRemark] = useState<string>("");
  const [unselectedBulkRemark, setUnselectedBulkRemark] = useState<string>("");

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<"ALL" | "APPROVED" | "NOT_APPROVED">("ALL");

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, query, activeFilter]);

  // Load initiated cycles list from API or fallback to localStorage
  useEffect(() => {
    const loadCycles = async () => {
      let loaded = false;
      try {
        const response = await Api.get(API_URLS.PROMOTION.CYCLES, true);
        if (response && response.data && Array.isArray(response.data.data)) {
          const rawList = response.data.data;
          const list = rawList.map((c: any) => ({
            id: c.id,
            name: c.cycleName || (c.gradeTo ? `${c.gradeFrom} to ${c.gradeTo} Promotion - ${c.quarter} ${c.financialYear}` : `${c.gradeFrom || ""} Promotion - ${c.quarter} ${c.financialYear}`),
            status: c.status
          }));
          setCyclesList(list);
          loaded = true;
          
          if (list.length > 0) {
            const queryId = queryParams.get("cycleId");
            if (!queryId && !location.state?.cycleId) {
              setSelectedCycleId(list[0].id);
            }
          }
        }
      } catch (error) {
      }

      if (!loaded) {
        const stored = localStorage.getItem("promotion_cycles");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const hasCycle1 = parsed.some((c: any) => c.id === 1);
              const list = hasCycle1 ? parsed : [{ id: 1, name: " " }, ...parsed];
              setCyclesList(list);
            }
          } catch (e) {
          }
        }
      }
    };
    loadCycles();
  }, []);

  // Sync state if URL / state changes externally
  useEffect(() => {
    const stateId = location.state?.cycleId;
    const queryId = queryParams.get("cycleId");
    const currentId = stateId || (queryId ? Number(queryId) : 1);
    if (currentId !== selectedCycleId) {
      setSelectedCycleId(currentId);
    }
  }, [location.state, location.search]);

  // Fetch employees checklist for the selected cycle using EVALUATE_TWO_STAGE
  const fetchChecks = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        Request: {
          data: {
            cycleId: Number(selectedCycleId)
          }
        }
      };
      const response = await Api.post(API_URLS.PROMOTION.EVALUATE_TWO_STAGE, payload, true);
      console.log("EVALUATE_TWO_STAGE Response (for Approval):", response);

      const resData = response?.data || response;
      const innerData = resData?.data || resData;
      let dataToSet: any[] = [];
      if (innerData && Array.isArray(innerData.eligibleByServiceWithEvaluations)) {
        dataToSet = innerData.eligibleByServiceWithEvaluations;
      } else if (resData && Array.isArray(resData.eligibleByServiceWithEvaluations)) {
        dataToSet = resData.eligibleByServiceWithEvaluations;
      } else if (innerData?.data && Array.isArray(innerData.data.eligibleByServiceWithEvaluations)) {
        dataToSet = innerData.data.eligibleByServiceWithEvaluations;
      } else if (Array.isArray(innerData)) {
        dataToSet = innerData;
      } else if (innerData && Array.isArray(innerData.data)) {
        dataToSet = innerData.data;
      } else if (resData && Array.isArray(resData.data)) {
        dataToSet = resData.data;
      } else if (typeof innerData === 'object' && innerData !== null) {
        const rawMerged: any[] = [];
        Object.keys(innerData).forEach(key => {
          if (Array.isArray(innerData[key])) {
            rawMerged.push(...innerData[key]);
          }
        });
        dataToSet = rawMerged;
      }

      setEmployees(dataToSet);
    } catch (error) {
      console.error("Failed to fetch checks", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCycleId]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const handleCycleChange = (id: number) => {
    setSelectedCycleId(id);
    navigate(`${location.pathname}?cycleId=${id}`, { replace: true, state: { cycleId: id } });
  };

  const formatDateDMY = (dateStr: any) => {
    if (!dateStr || dateStr === "N/A" || dateStr === "-" || dateStr === "null" || dateStr === "undefined") return "—";
    const str = String(dateStr).trim();
    if (!str) return "—";
    if (str.includes("T")) {
      const ymd = str.split("T")[0];
      const parts = ymd.split("-");
      if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (str.includes("-")) {
      const parts = str.split("-");
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      if (parts.length === 3 && parts[2].length === 4) return str;
    }
    return str;
  };

  const getEffectivePromotionDate = (e: any) => {
    const raw =
      e.adjustedPromotionDate ??
      e.adjusted_promotion_date ??
      e.effectivePromotionDate ??
      e.effective_promotion_date ??
      e.effectiveEligibilityDate ??
      e.effective_eligibility_date ??
      e.eligibilityDate ??
      e.baseDate ??
      e.base_date ??
      e.promotion_effective_date ??
      e.promotionEffectiveDate ??
      e.effectiveDate;

    if (!raw || raw === "N/A" || raw === "null" || raw === "undefined" || raw === "-") return "—";
    return formatDateDMY(raw);
  };

  const getDarBadgeDetails = (e: any) => {
    const rawStatus = (e.darStatus ?? e.dar_status ?? e.dar_evaluation?.status ?? e.dar ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "DAR CLEAR",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
      return {
        text: "ChargeSheet Issued",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
      return {
        text: "Minor Penalty",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
      return {
        text: "Major Penalty",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const getVigilanceBadgeDetails = (e: any) => {
    const rawStatus = (e.vigilance_evaluation?.status ?? e.vigilanceStatus ?? e.vigilance_status ?? e.vigilanceType ?? e.vigilance_type ?? e.vigilance ?? "").toString().trim();
    const statusLower = rawStatus.toLowerCase();

    if (statusLower === "no vigilance case" || statusLower === "no_vigilance_case" || statusLower === "clear" || statusLower === "cleared" || statusLower === "passed") {
      return {
        text: "No Vigilance Case",
        badgeClass: "bg-success text-white",
        badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "vigilance initiated" || statusLower === "vigilance_initiated") {
      return {
        text: "Vigilance Initiated",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "penalty awarded" || statusLower === "penalty_awarded") {
      return {
        text: "Penalty Awarded",
        badgeClass: "bg-danger text-white",
        badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    if (statusLower === "pending" || statusLower === "false") {
      return {
        text: "Pending",
        badgeClass: "bg-warning text-dark",
        badgeStyle: { backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
      };
    }
    return {
      text: rawStatus || "—",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }
    };
  };

  const formatEmployeeRow = (e: any, idx: number): EmployeeRow => {
    let joiningDate = e.joiningDate || e.joining_date || e.date_of_joining || e.dateOfJoining || e.doj || "";
    let lastPromoDate = e.lastPromoDate || e.last_promotion_date || e.last_promo_date || e.lastPromotion || "";
    const combined = e.joiningOrLastPromoDate || e.joining_or_last_promo_date || "";

    if ((!joiningDate || !lastPromoDate) && combined) {
      if (typeof combined === "string" && combined.includes("/")) {
        const parts = combined.split("/").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (typeof combined === "string" && combined.includes(",")) {
        const parts = combined.split(",").map((s: string) => s.trim());
        if (parts.length >= 2) {
          if (!joiningDate) joiningDate = parts[0];
          if (!lastPromoDate) lastPromoDate = parts[1];
        } else if (parts.length === 1 && !joiningDate) {
          joiningDate = parts[0];
        }
      } else if (!joiningDate) {
        joiningDate = combined;
      }
    }

    const formatJoiningDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "—";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    const formatLastPromoDate = (val: any) => {
      if (!val || val === "N/A" || val === "-" || val === "null" || val === "undefined") return "No Data Found";
      if (typeof val === "string" && val.includes("T")) return formatDateDMY(val.split("T")[0]);
      return formatDateDMY(val);
    };

    let category =
      e.category ||
      e.emp_category ||
      e.empCategory ||
      e.caste_category ||
      e.casteCategory ||
      e.cast_category ||
      e.castCategory ||
      e.caste ||
      e.social_category ||
      e.socialCategory ||
      "—";
    if (category === "N/A" || category === "null" || category === "undefined") category = "—";

    let desig = e.oldDesignation || e.old_designation || e.designationName || e.designation || e.newDesignation || e.new_designation || e.designationID || "—";
    if (typeof desig === "string" && desig.includes("-")) {
      const parts = desig.split("-").filter((p: string) => p !== "P");
      if (parts.length > 0) desig = parts.join("-");
    }

    let aparTotal = "—";
    if (e.apar_evaluation?.totalScore !== undefined && e.apar_evaluation?.totalScore !== null) {
      aparTotal = String(e.apar_evaluation.totalScore);
    } else if (e.apar_evaluation?.total_score !== undefined && e.apar_evaluation?.total_score !== null) {
      aparTotal = String(e.apar_evaluation.total_score);
    } else if (e.aparTotalScore !== undefined && e.aparTotalScore !== null) {
      aparTotal = String(e.aparTotalScore);
    } else if (e.apar_total_score !== undefined && e.apar_total_score !== null) {
      aparTotal = String(e.apar_total_score);
    }

    const lastPromoDateVal = formatLastPromoDate(lastPromoDate);
    const candidateKey = e.candidateId ? String(e.candidateId) : (e.id ? String(e.id) : (e.empId || e.emp_no || String(idx)));

    return {
      ...e,
      key: candidateKey,
      id: candidateKey,
      empNo: e.empId || e.emp_no || e.empNo || "—",
      name: e.employeeName || e.empName || e.emp_name || "—",
      category: String(category),
      designation: String(desig),
      joiningDate: formatJoiningDate(joiningDate),
      lastPromoDate: lastPromoDateVal !== "No Data Found" ? lastPromoDateVal : (e.joiningOrLastPromoDate || "No Data Found"),
      joiningOrLastPromoDate: e.joiningOrLastPromoDate || "—",
      serviceYears: e.serviceYears !== undefined && e.serviceYears !== null ? String(e.serviceYears) : (e.service_years !== undefined && e.service_years !== null ? String(e.service_years) : "—"),
      aparTotal: aparTotal,
      aparRemarks: e.aparRemarks ?? e.apar_remarks ?? e.aparRemark ?? e.apar_remark ?? "—",
      effectiveDate: getEffectivePromotionDate(e),
      listClass: e.listClassification || e.list_classification || e.finalDecision || e.status || "",
      dpcStatus: e.dpcStatus || e.dpc_status || "RECOMMENDED",
      dpcRemarks: e.dpcRemarks || e.dpc_remarks || e.remarks || e.reason || "suitable",
      approvalStatus: e.approvalStatus || e.approval_status || "PENDING"
    };
  };

  const parsedEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];
    return employees.map((e: any, idx: number) => formatEmployeeRow(e, idx));
  }, [employees]);

  // Do not auto-select by default; retain selection while moving between pages.
  useEffect(() => {
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);
  }, [parsedEmployees]);

  const handleApplySelectedBulkRemark = () => {
    if (!selectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };
    selectedRowKeys.forEach((key) => {
      updatedRemarks[key] = selectedBulkRemark;
    });
    setRemarks(updatedRemarks);
    setSelectedBulkRemark("");
    toast.success(`Remarks applied to ${selectedRowKeys.length} selected employee(s).`);
  };

  const handleApplyUnselectedBulkRemark = () => {
    if (!unselectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };

    const unselected = parsedEmployees.filter((emp) => {
      const isEvaluated = emp.approvalStatus === "APPROVED" || emp.approvalStatus === "DEFERRED" || emp.approvalStatus === "REJECTED";
      return !isEvaluated && !selectedRowKeys.includes(String(emp.id));
    });

    unselected.forEach((emp) => {
      updatedRemarks[emp.id] = unselectedBulkRemark;
    });

    setRemarks(updatedRemarks);
    setUnselectedBulkRemark("");
    toast.success(`Remarks applied to ${unselected.length} unselected employee(s).`);
  };

  const handleClearAllRemarks = () => {
    setRemarks({});
    toast.success("Cleared all custom remarks.");
  };

  const handleSubmitDpcApproval = async () => {
    setSubmitting(true);
    try {
      const approvals = parsedEmployees.map((emp) => {
        let isApproved = selectedRowKeys.includes(String(emp.id));
        if (emp.approvalStatus === "APPROVED") {
          isApproved = true;
        } else if (emp.approvalStatus === "DEFERRED" || emp.approvalStatus === "REJECTED") {
          isApproved = false;
        }
        return {
          candidateId: Number(emp.id),
          approved: isApproved,
          remarks: remarks[emp.id] || (isApproved ? "Approved by MD." : "Deferred due to administrative reasons.")
        };
      });

      const payload = {
        cycleId: selectedCycleId,
        approvals
      };
      await Api.post(API_URLS.PROMOTION.APPROVE, payload, true);

      toast.success("Competent Authority decisions submitted successfully!");
      fetchChecks();
    } catch (error: any) {
      toast.error("Failed to submit Competent Authority decisions: " + Api.getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const currentCycle = useMemo(() => {
    return cyclesList.find(c => Number(c.id) === selectedCycleId);
  }, [cyclesList, selectedCycleId]);

  const isReadOnly = useMemo(() => {
    if (parsedEmployees.length > 0 && parsedEmployees.every(e => e.approvalStatus === "APPROVED" || e.approvalStatus === "DEFERRED" || e.approvalStatus === "REJECTED")) {
      return true;
    }
    if (!currentCycle) return false;
    const status = (currentCycle.status || "").toUpperCase();
    return status === "APPROVED" || status === "SAP_SYNCED" || status === "COMPLETED";
  }, [currentCycle, parsedEmployees]);

  const filtered = useMemo(() => {
    let result = parsedEmployees;

    // Apply active filter
    if (activeFilter === "APPROVED") {
      result = result.filter(e => e.approvalStatus === "APPROVED");
    } else if (activeFilter === "NOT_APPROVED") {
      result = result.filter(e => e.approvalStatus === "DEFERRED" || e.approvalStatus === "REJECTED");
    }

    if (!query) return result;
    const q = query.toLowerCase();
    return result.filter((e: EmployeeRow) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.empNo || "").toLowerCase().includes(q) ||
      (e.designation || "").toLowerCase().includes(q)
    );
  }, [query, parsedEmployees, activeFilter]);

  const approvedCount = useMemo(() => {
    return parsedEmployees.filter(e => e.approvalStatus === "APPROVED").length;
  }, [parsedEmployees]);

  const notApprovedCount = useMemo(() => {
    return parsedEmployees.filter(e => e.approvalStatus === "DEFERRED" || e.approvalStatus === "REJECTED").length;
  }, [parsedEmployees]);

  const unselectedCount = useMemo(() => {
    return parsedEmployees.filter((emp) => {
      const isEvaluated = emp.approvalStatus === "APPROVED" || emp.approvalStatus === "DEFERRED" || emp.approvalStatus === "REJECTED";
      return !isEvaluated && !selectedRowKeys.includes(String(emp.id));
    }).length;
  }, [parsedEmployees, selectedRowKeys]);

  // Helper to format dates consistently as DD-MM-YY
  const formatDateDDMMYY = (dateVal: any): string => {
    if (!dateVal) return "—";
    const str = String(dateVal).trim();
    if (
      !str ||
      str === "—" ||
      str === "-" ||
      str.toLowerCase() === "n/a" ||
      str.toLowerCase() === "na" ||
      str.toLowerCase() === "null" ||
      str.toLowerCase() === "undefined" ||
      str.toLowerCase() === "no data found" ||
      str.toLowerCase() === "invalid date"
    ) {
      return "—";
    }

    if (str.includes(" / ")) {
      const parts = str.split(" / ").map((p) => formatDateDDMMYY(p)).filter((p) => p !== "—");
      if (parts.length === 0) return "—";
      if (parts.length === 1) return parts[0];
      if (parts[0] === parts[1]) return parts[0];
      return parts.join(" / ");
    }

    const cleanDateStr = str.includes("T") ? str.split("T")[0].trim() : str;

    const ymdMatch = cleanDateStr.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    if (ymdMatch) {
      const year = ymdMatch[1].slice(-2);
      const month = ymdMatch[2].padStart(2, "0");
      const day = ymdMatch[3].padStart(2, "0");
      return `${day}-${month}-${year}`;
    }

    const dmyMatch = cleanDateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const day = dmyMatch[1].padStart(2, "0");
      const month = dmyMatch[2].padStart(2, "0");
      const year = dmyMatch[3].slice(-2);
      return `${day}-${month}-${year}`;
    }

    const dmyShortMatch = cleanDateStr.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2})$/);
    if (dmyShortMatch) {
      const day = dmyShortMatch[1].padStart(2, "0");
      const month = dmyShortMatch[2].padStart(2, "0");
      const year = dmyShortMatch[3];
      return `${day}-${month}-${year}`;
    }

    const d = new Date(cleanDateStr);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    }

    return "—";
  };

  // Helper to determine whether employee is confirmed (Yes / No)
  const getWhetherConfirmed = (c: any): string => {
    const candidateFields = [
      c.whetherConfirmed,
      c.whether_confirmed,
      c.isConfirmed,
      c.is_confirmed,
      c.whetherProbationConfirmed,
      c.whether_probation_confirmed,
      c.probationConfirmed,
      c.probation_confirmed,
      c.confirmationStatus,
      c.confirmation_status,
      c.confirmed,
      c.probationStatus,
      c.probation_status,
      c.confirmation_evaluation?.isConfirmed,
      c.confirmation_evaluation?.status,
      c.probation_evaluation?.isConfirmed,
      c.probation_evaluation?.status,
      c.employee?.whetherConfirmed,
      c.employee?.whetherProbationConfirmed,
      c.employee?.isConfirmed,
      c.employee?.probationConfirmed,
      c.employee?.confirmationStatus
    ];

    for (const val of candidateFields) {
      if (val !== undefined && val !== null && val !== "") {
        if (typeof val === "boolean") return val ? "Yes" : "No";
        const str = String(val).trim().toLowerCase();
        if (
          str === "yes" ||
          str === "y" ||
          str === "confirmed" ||
          str === "true" ||
          str === "clear" ||
          str === "cleared" ||
          str === "passed"
        ) {
          return "Yes";
        }
        if (
          str === "no" ||
          str === "n" ||
          str === "unconfirmed" ||
          str === "not confirmed" ||
          str === "false" ||
          str === "failed" ||
          str === "pending" ||
          str === "probation" ||
          str === "extended"
        ) {
          return "No";
        }
      }
    }

    const confDate =
      c.confirmationDate ??
      c.confirmation_date ??
      c.dateOfConfirmation ??
      c.date_of_confirmation ??
      c.adjustedConfirmationDate ??
      c.adjusted_confirmation_date ??
      c.employee?.confirmationDate ??
      c.employee?.dateOfConfirmation;

    if (
      confDate &&
      confDate !== "—" &&
      confDate !== "-" &&
      confDate !== "N/A" &&
      confDate !== "null" &&
      confDate !== "undefined" &&
      confDate !== "No Data Found"
    ) {
      return "Yes";
    }

    return "Yes";
  };

  // Helper to determine vigilance clearance (Yes / No)
  const getVigClearanceStatus = (c: any): string => {
    if (c.vigilance_evaluation?.vigilanceCleared !== undefined) {
      if (!c.vigilance_evaluation.vigilanceCleared) return "No";
    }
    if (c.vigilanceCleared !== undefined) {
      if (!c.vigilanceCleared) return "No";
    }
    if (c.criminal_evaluation?.criminalCleared !== undefined) {
      if (!c.criminal_evaluation.criminalCleared) return "No";
    }
    if (c.criminalCleared !== undefined) {
      if (!c.criminalCleared) return "No";
    }

    const crimCase = c.criminalCase ?? c.criminal_case;
    if (crimCase !== undefined && crimCase !== null) {
      const crimStr = String(crimCase).trim().toLowerCase();
      if (crimStr === "yes" || crimStr === "y" || crimStr === "true") return "No";
    }

    const vigStatus = (
      c.vigilance_evaluation?.status ??
      c.vigilanceStatus ??
      c.vigilance_status ??
      c.vigilanceType ??
      c.vigilance_type ??
      c.vigilance ??
      "CLEAR"
    ).toString().trim().toLowerCase();

    if (
      vigStatus.includes("pending") ||
      vigStatus.includes("initiated") ||
      vigStatus.includes("penalty") ||
      vigStatus.includes("adverse") ||
      vigStatus.includes("case") ||
      vigStatus === "no" ||
      vigStatus === "false" ||
      vigStatus === "failed"
    ) {
      return "No";
    }

    return "Yes";
  };

  // Helper to test if a string represents an actual recorded remark
  const isActualRemark = (val: any): boolean => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    if (!str) return false;
    const lower = str.toLowerCase();
    const cleanLower = lower.replace(/[.\s]+$/, "").trim();

    if (
      str === "—" ||
      str === "-" ||
      str === "." ||
      cleanLower === "n/a" ||
      cleanLower === "na" ||
      cleanLower === "null" ||
      cleanLower === "undefined" ||
      cleanLower === "none" ||
      cleanLower === "no remarks" ||
      cleanLower === "no remark" ||
      cleanLower === "no data found" ||
      cleanLower === "apar records not available" ||
      cleanLower === "apar record not available" ||
      cleanLower === "records not available" ||
      cleanLower === "record not available" ||
      cleanLower === "eligible for promotion" ||
      cleanLower === "ineligible for promotion" ||
      cleanLower === "dar clear" ||
      cleanLower === "vigilance clear" ||
      cleanLower === "vigilance: clear" ||
      cleanLower === "criminal case: no" ||
      cleanLower === "criminal case: clear" ||
      cleanLower === "no vigilance or criminal case pending" ||
      cleanLower === "no vigilance or criminal case" ||
      cleanLower === "no vigilance case pending" ||
      cleanLower === "no vigilance case" ||
      cleanLower === "no vigilance" ||
      cleanLower === "no criminal case pending" ||
      cleanLower === "no criminal case" ||
      cleanLower === "vigilance: no vigilance or criminal case pending" ||
      cleanLower === "clear" ||
      cleanLower === "cleared" ||
      cleanLower === "passed" ||
      cleanLower === "failed" ||
      cleanLower === "fit" ||
      cleanLower === "not fit" ||
      cleanLower.startsWith("service shortfall:") ||
      cleanLower.startsWith("apar below benchmark:") ||
      cleanLower.startsWith("d&ar case pending") ||
      cleanLower.startsWith("vigilance clearance pending") ||
      cleanLower.startsWith("vigilance: no vigilance")
    ) {
      return false;
    }
    return true;
  };

  // Helper to consolidate remarks from D&AR, Vigilance, APAR, and manual entry
  const getConsolidatedRemarks = (c: any, userManualRemark?: string): string => {
    const parts: string[] = [];

    const rawDarRemark =
      c.darRemarks ??
      c.dar_remarks ??
      c.darRemark ??
      c.dar_remark ??
      c.dar_evaluation?.remarks ??
      c.dar_evaluation?.darRemarks ??
      c.dar_evaluation?.dar_remarks ??
      c.dar_evaluation?.remark ??
      c.dar_evaluation?.comments;

    if (isActualRemark(rawDarRemark)) {
      const cleanDarRemark = String(rawDarRemark).trim();
      if (cleanDarRemark) {
        parts.push(`D&AR: ${cleanDarRemark}`);
      }
    }

    const rawVigRemark =
      c.vigilanceRemarks ??
      c.vigilance_remarks ??
      c.vigRemarks ??
      c.vig_remarks ??
      c.vigilance_evaluation?.remarks ??
      c.vigilance_evaluation?.vigilanceRemarks ??
      c.vigilance_evaluation?.vigilance_remarks ??
      c.vigilance_evaluation?.remark;

    if (isActualRemark(rawVigRemark)) {
      const cleanVigRemark = String(rawVigRemark).trim();
      if (cleanVigRemark) {
        parts.push(`Vigilance: ${cleanVigRemark}`);
      }
    } else {
      // Include vigilance information ONLY when an actual vigilance/criminal case exists
      const crimCase = c.criminalCase ?? c.criminal_case;
      const crimStr = crimCase !== undefined && crimCase !== null ? String(crimCase).trim().toLowerCase() : "";
      const isCrimCase = crimStr === "yes" || crimStr === "y" || crimStr === "true";

      const vigStatus = (
        c.vigilance_evaluation?.status ??
        c.vigilanceStatus ??
        c.vigilance_status ??
        c.vigilanceType ??
        c.vigilance_type ??
        c.vigilance ??
        ""
      ).toString().trim();
      const vigStatusLower = vigStatus.toLowerCase();
      const isAdverseVig = (
        vigStatusLower.includes("pending") ||
        vigStatusLower.includes("initiated") ||
        vigStatusLower.includes("penalty") ||
        vigStatusLower.includes("adverse") ||
        vigStatusLower.includes("case") ||
        vigStatusLower === "failed" ||
        vigStatusLower === "no"
      ) && !vigStatusLower.includes("no vigilance") && !vigStatusLower.includes("no case");

      if (isCrimCase && isAdverseVig) {
        parts.push(`Vigilance: Adverse case (${vigStatus}) & Criminal case pending`);
      } else if (isCrimCase) {
        parts.push("Vigilance: Criminal case pending");
      } else if (isAdverseVig) {
        parts.push(`Vigilance: ${vigStatus}`);
      }
    }

    const rawAparRemark =
      c.aparRemarks ??
      c.apar_remarks ??
      c.aparRemark ??
      c.apar_remark ??
      c.apar_remark;

    if (isActualRemark(rawAparRemark)) {
      const cleanAparRemark = String(rawAparRemark).trim();
      if (cleanAparRemark) {
        parts.push(`APAR: ${cleanAparRemark}`);
      }
    }

    if (userManualRemark && isActualRemark(userManualRemark)) {
      const cleanManualRemark = String(userManualRemark).trim();
      if (cleanManualRemark) {
        parts.push(`DPC: ${cleanManualRemark}`);
      }
    }

    if (parts.length === 0) return "";
    return parts.join("\n");
  };

  // Helper to format APAR Total with denominator based on assessed financial years
  const getAparTotalWithDenominator = (c: any, fyKeys: string[]): string => {
    let aparScores: Record<string, any> = {};
    const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
    if (typeof rawScores === "string") {
      try { aparScores = JSON.parse(rawScores); } catch (e) {}
    } else if (rawScores && typeof rawScores === "object") {
      aparScores = rawScores;
    }

    let assessedYearsCount = 0;
    for (const fy of fyKeys) {
      const rawVal = aparScores[fy] !== undefined ? aparScores[fy] : c[fy];
      const formatted = formatAparScore(rawVal);
      if (formatted !== "-" && formatted !== "—" && formatted !== "") {
        const num = parseFloat(formatted);
        if (!isNaN(num) && num > 0) {
          assessedYearsCount++;
        }
      }
    }

    if (assessedYearsCount === 0 && Object.keys(aparScores).length > 0) {
      for (const [_, val] of Object.entries(aparScores)) {
        const rawVal = typeof val === "object" && val !== null ? (val.score ?? val.marks ?? val.value) : val;
        const formatted = formatAparScore(rawVal);
        if (formatted !== "-" && formatted !== "—" && formatted !== "") {
          const num = parseFloat(formatted);
          if (!isNaN(num) && num > 0) {
            assessedYearsCount++;
          }
        }
      }
    }

    const rawTotal = c.apar_evaluation?.totalScore ?? c.aparTotalScore ?? c.apar_total_score ?? c.aparTotal;
    const formattedTotal = formatAparScore(rawTotal);

    if (formattedTotal === "-" || formattedTotal === "—" || formattedTotal === "") {
      return "-";
    }

    if (String(formattedTotal).includes("/")) {
      return String(formattedTotal);
    }

    let denominator = assessedYearsCount * 10;
    const explicitMax = c.apar_evaluation?.maxScore ?? c.apar_evaluation?.maxMarks ?? c.apar_evaluation?.totalMaxScore;
    if (explicitMax !== undefined && explicitMax !== null && Number(explicitMax) > 0) {
      denominator = Number(explicitMax);
    } else if (c.apar_evaluation?.applicableYears !== undefined && Number(c.apar_evaluation.applicableYears) > 0) {
      denominator = Number(c.apar_evaluation.applicableYears) * 10;
    }

    if (denominator > 0) {
      return `${formattedTotal}/${denominator}`;
    }

    return formattedTotal;
  };

  const handleDownloadCandidates = () => {
    const listToExport = filtered.length > 0 ? filtered : parsedEmployees;
    if (!listToExport || listToExport.length === 0) {
      toast.warning("No candidate data available to export.");
      return;
    }

    const currentCycleObj = cyclesList.find((c: any) => Number(c.id) === Number(selectedCycleId));
    
    // Dynamic Title parameters
    const empType = currentCycleObj?.cadre || currentCycleObj?.employeeType || currentCycleObj?.gradeFrom || "Non-Executive";
    const reqYears = currentCycleObj?.requiredServiceYears || currentCycleObj?.minServiceYears || "05";
    let period = "";
    if (currentCycleObj?.quarter && currentCycleObj?.financialYear) {
      period = `${currentCycleObj.quarter} ${currentCycleObj.financialYear}`;
    } else if (currentCycleObj?.quarter || currentCycleObj?.financialYear) {
      period = `${currentCycleObj?.quarter || ""} ${currentCycleObj?.financialYear || ""}`.trim();
    } else {
      period = currentCycleObj?.name || "2025-2026";
    }

    const titleText = `Assessment Sheet for Promotion of ${empType} staff on completion of ${String(reqYears).padStart(2, "0")} years of service (${period})`;
    const subtitleText = "Revised APAR Criteria: Reserved Category-26/40 and Others-28/40";

    // Dynamic APAR Headers Extraction (4 keys)
    const extractFyKeys = (candidates: any[]) => {
      const fySet = new Set<string>();
      candidates.forEach((c) => {
        let aparScores: Record<string, any> = {};
        const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
        if (typeof rawScores === "string") {
          try { aparScores = JSON.parse(rawScores); } catch (e) {}
        } else if (rawScores && typeof rawScores === "object") {
          aparScores = rawScores;
        }
        Object.keys(aparScores).forEach((k) => fySet.add(k));
      });

      const sortedFy = Array.from(fySet).sort();
      if (sortedFy.length >= 4) return sortedFy.slice(-4);
      if (sortedFy.length > 0) return sortedFy;
      return ["2022-23", "2023-24", "2024-25", "2025-26"];
    };

    const fyKeys = extractFyKeys(listToExport);

    // Columns in exact order (20 columns after removing APAR Remarks and APAR Result)
    const headers = [
      "Sl. No.",
      "Emp No.",
      "Name",
      "Desig.",
      "Cat.",
      "Pay Scale",
      "Date of Joining / Last Promotion",
      "D&AR Clearance",
      "Vig. Clearance",
      "Whether Confirmed (Yes/No)",
      "LWP / LOP / Suspended Period",
      "Date of Eligibility for Promotion",
      `FY ${fyKeys[0] || "2022-23"}`,
      `FY ${fyKeys[1] || "2023-24"}`,
      `FY ${fyKeys[2] || "2024-25"}`,
      `FY ${fyKeys[3] || "2025-26"}`,
      "APAR Total",
      "Required APAR Benchmark",
      "Remarks",
      "Suitable / Not Suitable"
    ];

    const rows = listToExport.map((c: any, idx: number) => {
      const empNo = c.empId || c.emp_no || c.empNo || "—";
      const name = c.employeeName || c.empName || c.emp_name || c.name || "—";
      const desig = c.oldDesignation || c.old_designation || c.designationName || c.designation || "—";
      const cat = c.category || c.emp_category || c.empCategory || c.caste || "—";
      const payScale = c.currentPayscale || c.current_payscale || c.currentGrade || c.payScale || c.pay_scale || "—";
      
      const rawJoiningDate = c.joiningDate || c.joining_date || c.date_of_joining || c.dateOfJoining || c.doj || "";
      const rawLastPromoDate = c.lastPromoDate || c.last_promotion_date || c.last_promo_date || c.lastPromotion || "";
      const rawCombined = c.joiningOrLastPromoDate || c.joining_or_last_promo_date || "";

      let dojLastPromo = "";
      if (rawCombined && rawCombined !== "—" && rawCombined !== "-") {
        dojLastPromo = formatDateDDMMYY(rawCombined);
      } else {
        const jDate = formatDateDDMMYY(rawJoiningDate);
        const pDate = formatDateDDMMYY(rawLastPromoDate);
        if (jDate !== "—" && pDate !== "—") {
          dojLastPromo = jDate === pDate ? jDate : `${jDate} / ${pDate}`;
        } else if (pDate !== "—") {
          dojLastPromo = pDate;
        } else if (jDate !== "—") {
          dojLastPromo = jDate;
        } else {
          dojLastPromo = "—";
        }
      }

      // D&AR Clearance multiline cell content
      const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? c.dar ?? "").toString().trim();
      const darStatusLower = rawDarStatus.toLowerCase();
      const rawCaseNo = c.caseNo ?? c.case_no ?? c.dar_evaluation?.caseNo;
      let validCaseNo = "";
      if (rawCaseNo !== null && rawCaseNo !== undefined) {
        const strCaseNo = String(rawCaseNo).trim();
        const lowerCaseNo = strCaseNo.toLowerCase();
        if (
          strCaseNo &&
          strCaseNo !== "—" &&
          strCaseNo !== "-" &&
          strCaseNo !== "." &&
          lowerCaseNo !== "n/a" &&
          lowerCaseNo !== "na" &&
          lowerCaseNo !== "null" &&
          lowerCaseNo !== "undefined" &&
          lowerCaseNo !== "none" &&
          lowerCaseNo !== "no case" &&
          lowerCaseNo !== "no data found"
        ) {
          validCaseNo = strCaseNo;
        }
      }

      const csDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.dar_evaluation?.chargeSheetIssuedDate);
      const penaltyDesc = c.penaltyDescription ?? c.penalty_description ?? c.dar_evaluation?.penaltyDescription ?? "—";
      const dateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? c.dar_evaluation?.dateOfAction);
      const startDate = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? c.dar_evaluation?.penaltyStartDate);
      const endDate = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? c.dar_evaluation?.penaltyEndDate);

      const darLines: string[] = [];
      if (darStatusLower === "dar clear" || darStatusLower === "dar_clear" || darStatusLower === "clear" || darStatusLower === "cleared" || darStatusLower === "passed") {
        darLines.push("DAR CLEAR");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
      } else if (darStatusLower.includes("chargesheet")) {
        darLines.push("ChargeSheet Issued");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (csDate && csDate !== "—") {
          darLines.push(`Charge Sheet Date: ${csDate}`);
        }
      } else if (darStatusLower.includes("minor")) {
        darLines.push("Minor Penalty");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (dateOfAction && dateOfAction !== "—") {
          darLines.push(`Date of Action: ${dateOfAction}`);
        }
        if (penaltyDesc && penaltyDesc !== "—") {
          darLines.push(`Penalty: ${penaltyDesc}`);
        }
      } else if (darStatusLower.includes("major")) {
        darLines.push("Major Penalty");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
        if (startDate && startDate !== "—") {
          darLines.push(`Start Date: ${startDate}`);
        }
        if (endDate && endDate !== "—") {
          darLines.push(`End Date: ${endDate}`);
        }
        if (penaltyDesc && penaltyDesc !== "—") {
          darLines.push(`Penalty: ${penaltyDesc}`);
        }
      } else {
        darLines.push(rawDarStatus || "Pending");
        if (validCaseNo) {
          darLines.push(`Case No.: ${validCaseNo}`);
        }
      }

      const darTextCell = darLines.join('<br style="mso-data-placement:same-cell;"/>');

      // Vigilance Clearance - strictly Yes or No
      const vigClearance = getVigClearanceStatus(c);

      // Confirmation Status - strictly Yes or No
      const whetherConfirmed = getWhetherConfirmed(c);

      const suitableNotSuitable = ""; // Left blank for manual decision

      // LWP / LOP / Suspended Period
      const lwp = c.lwpDays ?? c.lwp_days ?? c.lwp ?? 0;
      const suspension = c.suspensionDays ?? c.suspension_days ?? c.suspension ?? 0;
      let lwpCell = "0 days";
      if (lwp > 0 && suspension > 0) lwpCell = `LWP: ${lwp} days, Suspension: ${suspension} days`;
      else if (lwp > 0) lwpCell = `LWP: ${lwp} days`;
      else if (suspension > 0) lwpCell = `Suspension: ${suspension} days`;

      // Date of Eligibility
      const eligDate = getEffectivePromotionDate(c);

      // APAR scores per FY
      let aparScores: Record<string, any> = {};
      const rawScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
      if (typeof rawScores === "string") {
        try { aparScores = JSON.parse(rawScores); } catch (e) {}
      } else if (rawScores && typeof rawScores === "object") {
        aparScores = rawScores;
      }

      const fyScore1 = formatAparScore(aparScores[fyKeys[0]]);
      const fyScore2 = formatAparScore(aparScores[fyKeys[1]]);
      const fyScore3 = formatAparScore(aparScores[fyKeys[2]]);
      const fyScore4 = formatAparScore(aparScores[fyKeys[3]]);

      // Total APAR Marks with applicable maximum
      const aparTotal = getAparTotalWithDenominator(c, fyKeys);
      const reqBenchmark = c.apar_evaluation?.requiredBenchmark ?? c.aparBenchmark ?? "32.00";

      // Consolidated Remarks: populated ONLY with actual recorded remarks, otherwise blank
      const consolidatedRemarks = getConsolidatedRemarks(c, remarks[c.id]);

      return {
        srNo: idx + 1,
        empNo,
        name,
        desig,
        cat,
        payScale,
        dojLastPromo,
        darTextCell,
        vigClearance,
        whetherConfirmed,
        lwpCell,
        eligDate,
        fyScore1,
        fyScore2,
        fyScore3,
        fyScore4,
        aparTotal,
        reqBenchmark,
        consolidatedRemarks,
        suitableNotSuitable
      };
    });

    const totalCols = headers.length;
    const cycleLabel = currentCycleObj?.name ? currentCycleObj.name.replace(/[^a-zA-Z0-9_-]/g, "_") : `cycle_${selectedCycleId}`;
    const dateStr = new Date().toISOString().split("T")[0];

    const htmlTable = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <!--[if gte mso 9]>
  <xml>
    <x:ExcelWorkbook>
      <x:ExcelWorksheets>
        <x:ExcelWorksheet>
          <x:Name>DPC Assessment Sheet</x:Name>
          <x:WorksheetOptions>
            <x:Print>
              <x:ValidPrinterInfo/>
              <x:PaperSizeIndex>9</x:PaperSizeIndex>
              <x:Scale>65</x:Scale>
            </x:Print>
            <x:PageSetup>
              <x:Layout x:Orientation="Landscape"/>
              <x:Header x:Data="&amp;C&amp;&quot;Calibri,Bold&quot;&amp;11 ${titleText}&amp;#10;&amp;&quot;Calibri,Regular&quot;&amp;9 ${subtitleText}&amp;R&amp;P of &amp;N"/>
              <x:RepeatRows x:Data="$1:$3"/>
            </x:PageSetup>
            <x:DisplayGridlines/>
            <x:FreezePanes/>
            <x:FrozenNoSplit/>
            <x:SplitHorizontal>3</x:SplitHorizontal>
            <x:TopRowBottomPane>3</x:TopRowBottomPane>
            <x:ActivePane>2</x:ActivePane>
          </x:WorksheetOptions>
        </x:ExcelWorksheet>
      </x:ExcelWorksheets>
    </x:ExcelWorkbook>
  </xml>
  <![endif]-->
  <style>
    @page {
      size: landscape;
      margin: 0.5in 0.3in 0.6in 0.3in;
      mso-page-orientation: landscape;
      mso-header-data: "&C&\"Calibri,Bold\"&11 ${titleText}\\000A&\"Calibri,Regular\"&9 ${subtitleText}&R&P of &N";
    }
    table { border-collapse: collapse; width: 100%; font-family: Calibri, Arial, sans-serif; font-size: 9.5pt; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; }
    th.title-header { background-color: #1e3a8a !important; color: #ffffff !important; font-size: 13pt; font-weight: bold; text-align: center; padding: 12px; border: 1.5px solid #1e3a8a !important; }
    th.subtitle-header { background-color: #f1f5f9 !important; color: #334155 !important; font-size: 9.5pt; font-weight: bold; text-align: center; padding: 6px; border: 1px solid #cbd5e1 !important; }
    th.col-header { background-color: #185FA5 !important; color: #ffffff !important; font-weight: bold; text-align: center; border: 1px solid #0f172a !important; padding: 8px 6px; font-size: 9.5pt; vertical-align: middle; white-space: nowrap; }
    td { border: 1px solid #cbd5e1 !important; padding: 6px 8px; vertical-align: middle; font-size: 9.5pt; color: #0f172a; }
    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .text-bold { font-weight: bold; }
    .row-even { background-color: #f8fafc; }
    .fit-cell { background-color: #dcfce7 !important; color: #15803d !important; font-weight: bold; text-align: center; }
    .not-fit-cell { background-color: #fee2e2 !important; color: #b91c1c !important; font-weight: bold; text-align: center; }
  </style>
</head>
<body>
  <table border="1">
    <thead>
      <tr>
        <th colspan="${totalCols}" class="title-header">${titleText}</th>
      </tr>
      <tr>
        <th colspan="${totalCols}" class="subtitle-header">${subtitleText}</th>
      </tr>
      <tr>
        ${headers.map((h) => `<th class="col-header">${h}</th>`).join("")}
      </tr>
    </thead>
    <tbody>
      ${rows
        .map((r, idx) => {
          const rowClass = idx % 2 === 1 ? ' class="row-even"' : "";
          const confClass = r.whetherConfirmed === "Yes" ? 'class="fit-cell"' : 'class="not-fit-cell"';
          const vigClass = r.vigClearance === "Yes" ? 'class="text-center text-bold"' : 'class="not-fit-cell"';
          const isPageBreak = (idx + 1) % 10 === 0 && idx + 1 < rows.length;
          const trStyle = `height: 46pt; mso-height-source: userset;${isPageBreak ? " page-break-after: always; mso-special-format: page-break;" : ""}`;

          return `<tr${rowClass} style="${trStyle}">
            <td class="text-center">${r.srNo}</td>
            <td style="mso-number-format:'\\@';" class="text-center text-bold">${r.empNo}</td>
            <td class="text-bold">${r.name}</td>
            <td class="text-center">${r.desig}</td>
            <td class="text-center" style="white-space: nowrap;">${r.cat}</td>
            <td class="text-center">${r.payScale}</td>
            <td class="text-center" style="white-space: nowrap;">${r.dojLastPromo}</td>
            <td style="white-space: pre-wrap; mso-data-placement: same-cell;">${r.darTextCell}</td>
            <td ${vigClass}>${r.vigClearance}</td>
            <td ${confClass}>${r.whetherConfirmed}</td>
            <td class="text-center">${r.lwpCell}</td>
            <td class="text-center">${r.eligDate}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore1}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore2}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore3}</td>
            <td class="text-center" style="white-space: nowrap;">${r.fyScore4}</td>
            <td class="text-center text-bold" style="white-space: nowrap;">${r.aparTotal}</td>
            <td class="text-center">${r.reqBenchmark}</td>
            <td style="white-space: pre-wrap; mso-data-placement: same-cell; min-width: 180px; width: 220px;">${r.consolidatedRemarks}</td>
            <td class="text-center">${r.suitableNotSuitable}</td>
          </tr>`;
        })
        .join("\n")}
    </tbody>
  </table>
</body>
</html>`;

    const blob = new Blob([htmlTable], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Competent_Authority_Assessment_Sheet_${cycleLabel}_${dateStr}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("DPC Assessment Sheet downloaded successfully!");
  };

  // Table Columns Configuration
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 70
      },
      { title: "Emp No.", dataIndex: "empNo" },
      { title: "Name", dataIndex: "name", render: (v: string) => <div style={{ fontWeight: 600 }}>{v}</div> },
      { title: "Category", dataIndex: "category" },
      { title: "Designation", dataIndex: "designation" },
      { title: "Date of Joining", dataIndex: "joiningDate" },
      { title: "Last Promo Date", dataIndex: "lastPromoDate" },
      { title: "Service (Years)", dataIndex: "serviceYears" },
      {
        title: "APAR Total",
        dataIndex: "aparTotal",
        render: (v: string, record: EmployeeRow) => {
          const isFailed = record.apar_evaluation?.status === "FAILED" || record.apar_evaluation?.isSuitable === false;
          return <span className={isFailed ? "text-danger fw-bold" : "text-success fw-bold"}>{formatAparScore(v)}</span>;
        }
      },
      {
        title: "APAR Remarks",
        dataIndex: "aparRemarks",
        render: (v: string, record: EmployeeRow) => {
          const remarks = v || record.aparRemarks || record.apar_remarks || record.aparRemark || record.apar_remark || "—";
          return (
            <div style={{ maxWidth: "220px", whiteSpace: "normal", wordBreak: "break-word" }}>
              {remarks}
            </div>
          );
        }
      },
      {
        title: "D&AR",
        dataIndex: "dar",
        render: (_: any, record: EmployeeRow) => {
          const darBadge = getDarBadgeDetails(record);
          return <span className={`badge ${darBadge.badgeClass}`} style={darBadge.badgeStyle}>{darBadge.text}</span>;
        }
      },
      {
        title: "Vigilance",
        dataIndex: "vigilance",
        render: (_: any, record: EmployeeRow) => {
          const vigBadge = getVigilanceBadgeDetails(record);
          return <span className={`badge ${vigBadge.badgeClass}`} style={vigBadge.badgeStyle}>{vigBadge.text}</span>;
        }
      },
      { title: "Promotion Effective Date", dataIndex: "effectiveDate" },
      {
        title: "Evaluation Details",
        dataIndex: "evaluationDetails",
        render: (_: any, record: EmployeeRow) => (
          <button
            type="button"
            className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
            style={{ fontSize: "11px", borderRadius: "5px", whiteSpace: "nowrap" }}
            onClick={() => {
              setSelectedEvaluationCandidate(record);
              setActiveModalTab("apar");
            }}
          >
            <i className="ti ti-eye fs-6"></i> View Details
          </button>
        )
      },
      {
        title: "DPC status",
        dataIndex: "dpcStatus",
        render: (v: string) => (
          <span className={`badge ${v === "RECOMMENDED" ? "bg-success" : v === "NOT RECOMMENDED" ? "bg-danger" : "bg-warning"}`}>{v}</span>
        )
      },
      { title: "DPC Remarks", dataIndex: "dpcRemarks", render: (v: string) => v || "suitable" },
      {
        title: "Approval Remarks",
        dataIndex: "remarks",
        render: (_: any, record: EmployeeRow) => (
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Enter approval remark"
            value={remarks[record.id] !== undefined ? remarks[record.id] : (record.approvalRemarks || "")}
            onChange={(e) => {
              setRemarks(prev => ({
                ...prev,
                [record.id]: e.target.value
              }));
            }}
            disabled={isReadOnly || record.approvalStatus === "APPROVED" || record.approvalStatus === "DEFERRED" || record.approvalStatus === "REJECTED"}
            style={{ minWidth: 200 }}
          />
        )
      }
    ];

    return cols;
  }, [remarks, isReadOnly, pagination]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Competent Authority Review</h3>
            <p className="text-muted small mb-0">Review and approve or defer recommendations from DPC</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedCycleId}
              onChange={(e) => handleCycleChange(Number(e.target.value))}
              style={{ minWidth: 240, height: 36 }}
            >
              {cyclesList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {!isReadOnly && (
              <button
                className="btn btn-success btn-sm"
                onClick={handleSubmitDpcApproval}
                disabled={submitting || parsedEmployees.length === 0}
                style={{ height: 36, whiteSpace: 'nowrap' }}
              >
                {submitting ? "Submitting..." : "Submit Approval"}
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <h5 className="mb-3">Reviewed Eligible Candidates</h5>

            {/* SEARCH AND FILTER BAR */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3 p-3 bg-light rounded-3 shadow-xs">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "560px" }}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="ti ti-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search name, emp no or designation..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {(approvedCount > 0 || notApprovedCount > 0) && (
                  <span className="text-muted small fw-bold text-uppercase me-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Filter:</span>
                )}
                {approvedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "APPROVED" ? "btn-success text-white shadow-sm" : "btn-outline-success"
                      }`}
                    onClick={() => setActiveFilter(activeFilter === "APPROVED" ? "ALL" : "APPROVED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "APPROVED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-check" style={{ fontSize: "14px" }}></i>
                    <span>Approved</span>
                    <span className={`badge rounded-pill ${activeFilter === "APPROVED" ? "bg-white text-success" : "bg-success text-white"}`}>
                      {approvedCount}
                    </span>
                  </button>
                )}
                {notApprovedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "NOT_APPROVED" ? "btn-danger text-white shadow-sm" : "btn-outline-danger"
                      }`}
                    onClick={() => setActiveFilter(activeFilter === "NOT_APPROVED" ? "ALL" : "NOT_APPROVED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "NOT_APPROVED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-x" style={{ fontSize: "14px" }}></i>
                    <span>Not Approved</span>
                    <span className={`badge rounded-pill ${activeFilter === "NOT_APPROVED" ? "bg-white text-danger" : "bg-danger text-white"}`}>
                      {notApprovedCount}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* BULK REMARKS ACTIONS */}
            {!isReadOnly && (
              <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1 me-2">
                    <i className="ti ti-edit text-muted" style={{ fontSize: "14px" }}></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>Bulk Remarks:</span>
                  </div>

                  {/* Selected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for selected..."
                      value={selectedBulkRemark}
                      onChange={(e) => setSelectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-primary btn-sm px-2.5"
                      onClick={handleApplySelectedBulkRemark}
                      disabled={!selectedBulkRemark.trim() || selectedRowKeys.length === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Selected ({selectedRowKeys.length})
                    </button>
                  </div>

                  <div className="d-none d-md-block" style={{ width: "1px", height: "20px", backgroundColor: "#e2e8f0", margin: "0 8px" }}></div>

                  {/* Unselected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for unselected..."
                      value={unselectedBulkRemark}
                      onChange={(e) => setUnselectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-secondary text-white btn-sm px-2.5"
                      onClick={handleApplyUnselectedBulkRemark}
                      disabled={!unselectedBulkRemark.trim() || unselectedCount === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Unselected ({unselectedCount})
                    </button>
                  </div>

                  <button
                    className="btn btn-link btn-sm text-muted ms-auto py-0"
                    onClick={handleClearAllRemarks}
                    style={{ fontSize: "11px", textDecoration: "none" }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            <Table
              ref={tableRef}
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              Selection={true}
              selectAllAcrossPages={true}
              onRowSelection={(keys) => setSelectedRowKeys(keys)}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
              getCheckboxProps={(record: any) => {
                const isEvaluated = record.approvalStatus === "APPROVED" || record.approvalStatus === "DEFERRED" || record.approvalStatus === "REJECTED";
                return {
                  disabled: isReadOnly || isEvaluated,
                  className: (isReadOnly || isEvaluated) ? "d-none" : "",
                };
              }}
            >
              <div className="d-flex align-items-center justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                  style={{ height: "31px", fontSize: "12px", borderRadius: "6px" }}
                  onClick={handleDownloadCandidates}
                  title="Download Candidates"
                >
                  <i className="ti ti-download" style={{ fontSize: "13px" }} />
                  <span>Download</span>
                </button>
                <div id="DataTables_Table_0_filter" className="dataTables_filter mb-0">
                  <label className="mb-0">
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      placeholder="Search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </label>
                </div>
              </div>
            </Table>
          </div>
        </div>

        {/* Evaluation Details Modal */}
        {selectedEvaluationCandidate && (() => {
          const c = selectedEvaluationCandidate;
          const empIdVal = String(c.empNo || c.empId || c.emp_no || "—");
          const empNameVal = String(c.name || c.employeeName || c.empName || c.emp_name || "—");

          // Parse APAR scores
          let aparScores: Record<string, any> = {};
          const rawAparScores = c.apar_evaluation?.scores ?? c.apar_scores ?? c.aparScores ?? c.year_scores ?? c.yearScores;
          if (typeof rawAparScores === "string") {
            try {
              aparScores = JSON.parse(rawAparScores);
            } catch (e) {
              aparScores = {};
            }
          } else if (rawAparScores && typeof rawAparScores === "object") {
            aparScores = rawAparScores;
          }

          // D&AR status details
          const dar = getDarBadgeDetails(c);
          const rawDarStatus = (c.darStatus ?? c.dar_status ?? c.dar_evaluation?.status ?? "").toString().trim();
          const darStatusLower = rawDarStatus.toLowerCase();
          const isChargeSheet = darStatusLower === "chargesheet issued" || darStatusLower === "chargesheet_issued" || darStatusLower === "chargesheetissued";
          const isMinor = darStatusLower === "minor penalty" || darStatusLower === "minor_penalty" || darStatusLower === "minorpenalty";
          const isMajor = darStatusLower === "major penalty" || darStatusLower === "major_penalty" || darStatusLower === "majorpenalty";
          const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

          const darCaseNo = c.caseNo ?? c.case_no ?? c.dar_evaluation?.caseNo ?? "—";
          const darChargeSheetDate = formatDateDMY(c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.dar_evaluation?.chargeSheetIssuedDate);
          const darPenaltyDesc = c.penaltyDescription ?? c.penalty_description ?? c.dar_evaluation?.penaltyDescription ?? "—";
          const darDateOfAction = formatDateDMY(c.dateOfAction ?? c.date_of_action ?? c.dar_evaluation?.dateOfAction);
          const darPenaltyStart = formatDateDMY(c.penaltyStartDate ?? c.penalty_start_date ?? c.dar_evaluation?.penaltyStartDate);
          const darPenaltyEnd = formatDateDMY(c.penaltyEndDate ?? c.penalty_end_date ?? c.dar_evaluation?.penaltyEndDate);

          // Vigilance details
          const vigStatus = c.vigilance_evaluation?.status ?? c.vigilanceStatus ?? c.vigilance_status ?? "—";
          const vigClearedStr = c.vigilance_evaluation?.vigilanceCleared !== undefined
            ? (c.vigilance_evaluation.vigilanceCleared ? "Cleared" : "Not Cleared")
            : (c.vigilanceCleared !== undefined ? (c.vigilanceCleared ? "Cleared" : "Not Cleared") : "—");

          const crimStatus = c.criminal_evaluation?.status ?? c.criminalStatus ?? c.criminal_status ?? "—";
          const crimClearedStr = c.criminal_evaluation?.criminalCleared !== undefined
            ? (c.criminal_evaluation.criminalCleared ? "Cleared" : "Not Cleared")
            : (c.criminalCleared !== undefined ? (c.criminalCleared ? "Cleared" : "Not Cleared") : "—");

          const vigType = c.vigilanceType ?? c.vigilance_type ?? "—";
          const crimCaseVal = c.criminalCase !== undefined
            ? (c.criminalCase === "yes" || c.criminalCase === "Yes" ? "Yes" : (c.criminalCase === "no" || c.criminalCase === "No" ? "No" : String(c.criminalCase)))
            : (c.criminal_case !== undefined ? (c.criminal_case === "yes" || c.criminal_case === "Yes" ? "Yes" : (c.criminal_case === "no" || c.criminal_case === "No" ? "No" : String(c.criminal_case))) : "—");
          const vigRemarks = c.vigilanceRemarks ?? c.vigilance_remarks ?? "—";

          return (
            <div
              className="modal fade show d-block"
              tabIndex={-1}
              style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
              onClick={() => setSelectedEvaluationCandidate(null)}
            >
              <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={{ maxWidth: "620px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
                  {/* Header */}
                  <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                    <div>
                      <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                        <i className="ti ti-file-analytics text-primary fs-5"></i>
                        Candidate Evaluation Details
                      </h6>
                      <div className="text-muted small mt-1">
                        Employee: <span className="fw-bold text-dark">{empNameVal}</span> ({empIdVal})
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setSelectedEvaluationCandidate(null)}
                      aria-label="Close"
                    ></button>
                  </div>

                  {/* Navigation Tabs */}
                  <div className="px-4 pt-3 bg-white border-bottom">
                    <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-3 mb-0">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "apar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("apar")}
                        >
                          <i className="ti ti-file-text me-1.5"></i> APAR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("dar")}
                        >
                          <i className="ti ti-scale me-1.5"></i> D&AR Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                          style={{ fontSize: "13px" }}
                          onClick={() => setActiveModalTab("vigilance")}
                        >
                          <i className="ti ti-shield-check me-1.5"></i> Vigilance Details
                        </button>
                      </li>
                    </ul>
                  </div>

                  {/* Body Content */}
                  <div className="modal-body p-4" style={{ minHeight: "260px" }}>
                    {/* Tab 1: APAR Details */}
                    {activeModalTab === "apar" && (
                      <div>
                        <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                          Financial Year APAR Marks
                        </h6>
                        {Object.keys(aparScores).length === 0 ? (
                          <div className="alert alert-light border text-muted small p-3 rounded">
                            No individual financial year APAR scores provided.
                          </div>
                        ) : (
                          <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "14px" }}>
                              <thead style={{ backgroundColor: "#f1f5f9" }}>
                                <tr>
                                  <th className="py-2.5 px-3" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>Financial Year</th>
                                  <th className="py-2.5 px-3 text-end" style={{ color: "#334155", fontWeight: 700, fontSize: "13px" }}>APAR Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(aparScores).map(([fy, score]) => {
                                  const rawScoreVal = typeof score === "object" && score !== null ? (score.score ?? score.marks ?? score.value ?? score.rating) : score;
                                  const numericScore = parseFloat(String(rawScoreVal));
                                  const isZero = isNaN(numericScore) || numericScore === 0;

                                  return (
                                    <tr key={fy}>
                                      <td className="py-2.5 px-3 fw-semibold text-dark" style={{ fontSize: "14px" }}>{fy}</td>
                                      <td className="py-2.5 px-3 text-end fw-bold" style={{ fontSize: "16px", color: isZero ? "#64748b" : "#2563eb" }}>
                                        {isZero ? "-" : String(rawScoreVal ?? "-")}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        <div className="row g-3 mt-1">
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Total APAR Score</div>
                              <div className="fw-bold mt-1" style={{ fontSize: "22px", lineHeight: "1.2", color: (c.apar_evaluation?.status === "FAILED" || c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {formatAparScore(c.apar_evaluation?.totalScore ?? c.aparTotalScore ?? c.apar_total_score ?? c.aparTotal)}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Required Benchmark</div>
                              <div className="fw-bold text-dark mt-1" style={{ fontSize: "22px", lineHeight: "1.2" }}>
                                {c.apar_evaluation?.requiredBenchmark ?? c.aparBenchmark ?? "32.00"}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Result</div>
                              <div>
                                <span className={`badge ${c.apar_evaluation?.status === "FAILED" ? "bg-danger" : "bg-success"}`} style={{ fontSize: "12px", padding: "5px 12px", fontWeight: 700 }}>
                                  {c.apar_evaluation?.status ?? c.aparStatus ?? "PASSED"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Suitability</div>
                              <div className="fw-bold" style={{ fontSize: "15px", color: (c.apar_evaluation?.isSuitable === false) ? "#dc2626" : "#16a34a" }}>
                                {c.apar_evaluation?.isSuitable !== undefined
                                  ? (c.apar_evaluation.isSuitable ? "Suitable" : "Not Suitable")
                                  : (c.isSuitable !== undefined ? (c.isSuitable ? "Suitable" : "Not Suitable") : "Suitable")}
                              </div>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                              <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>APAR Remarks</div>
                              <div className="fw-semibold text-dark" style={{ fontSize: "13px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {c.aparRemarks ?? c.apar_remarks ?? c.aparRemark ?? c.apar_remark ?? "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tab 2: D&AR Details */}
                    {activeModalTab === "dar" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">D&AR Status</span>
                          <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
                            {dar.text}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Case No.</span>
                          <span className="fw-semibold text-dark">{darCaseNo}</span>
                        </div>

                        {(isChargeSheet || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                            <span className="fw-semibold text-dark">{darChargeSheetDate}</span>
                          </div>
                        )}

                        {(isMinor || isMajor || isClearOrNone) && (
                          <div className="pb-2 border-bottom">
                            <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                            <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                              {darPenaltyDesc}
                            </div>
                          </div>
                        )}

                        {(isMinor || isClearOrNone) && (
                          <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                            <span className="text-secondary font-medium">Date of Action</span>
                            <span className="fw-semibold text-dark">{darDateOfAction}</span>
                          </div>
                        )}

                        {(isMajor || isClearOrNone) && (
                          <>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty Start Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyStart}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                              <span className="text-secondary font-medium">Penalty End Date</span>
                              <span className="fw-semibold text-dark">{darPenaltyEnd}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Tab 3: Vigilance Details */}
                    {activeModalTab === "vigilance" && (
                      <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Status</span>
                          <span className="fw-semibold text-dark">{vigStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Cleared</span>
                          <span className={`badge ${vigClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {vigClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Status</span>
                          <span className="fw-semibold text-dark">{crimStatus}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case Cleared</span>
                          <span className={`badge ${crimClearedStr === "Cleared" ? "bg-success" : "bg-danger"}`}>
                            {crimClearedStr}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Vigilance Type</span>
                          <span className="fw-semibold text-dark">{vigType}</span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Criminal Case</span>
                          <span className="fw-semibold text-dark">{crimCaseVal}</span>
                        </div>

                        <div className="pb-2">
                          <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                          <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                            {vigRemarks}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default DpcApprovalList;

